/**
 * AITutorPanel (Sprint 7 B5; phiên chat 2026-10-04) — Drawer side panel chat với AI Tutor.
 *
 * Props:
 *   - open: bool — Drawer visibility
 *   - onClose: callback close
 *   - lessonSlug: phạm vi phiên chat — liệt kê + tạo phiên mới theo bài này
 *   - conversationId: optional — phiên mở sẵn (mặc định: phiên gần nhất)
 *   - lessonTitle: optional context (hiển thị trong header)
 *   - suggestedQuestions: optional override default 3 câu
 *
 * Phiên chat: nút "Phiên mới" mở phiên trống — conversation chỉ được tạo khi gửi câu đầu tiên
 * (không sinh phiên rỗng); nút "Các phiên" liệt kê phiên cũ để xem lại / hỏi tiếp. Khoá đổi
 * phiên khi đang nhận trả lời (stream gắn với phiên đang mở).
 *
 * Layout: Drawer right 480px (≤ màn hình điện thoại):
 *   - Header: tiêu đề + lesson info + nút Phiên mới / Các phiên
 *   - MessageList (flex: 1, scrollable) — hoặc SessionList khi xem các phiên
 *   - ChatInput (sticky bottom) — rich editor gọn, câu hỏi gửi dạng markdown
 */

import { useState } from "react";
import { Alert, Button, Drawer, Space, Tooltip, Typography, message } from "antd";
import { HistoryOutlined, PlusOutlined, RobotOutlined } from "@ant-design/icons";
import { useConversations, useCreateConversation, useMessages } from "@/api/learning";
import { ChatInput } from "./ChatInput";
import { MessageList } from "./MessageList";
import { SessionList } from "./SessionList";
import { useStreamingChat } from "./useStreamingChat";

const { Text, Title } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  lessonSlug?: string;
  conversationId?: number;
  lessonTitle?: string;
  suggestedQuestions?: string[];
  width?: number | string;
}

export function AITutorPanel({
  open,
  onClose,
  lessonSlug,
  conversationId,
  lessonTitle,
  suggestedQuestions,
  width = "min(480px, 100vw)",
}: Props) {
  const { data: convs, isLoading: convsLoading } = useConversations(lessonSlug);
  const createConv = useCreateConversation();
  // null = phiên gần nhất; "new" = phiên mới chưa gửi câu nào (tạo khi gửi câu đầu tiên).
  const [selected, setSelected] = useState<number | "new" | null>(conversationId ?? null);
  const [view, setView] = useState<"chat" | "sessions">("chat");

  const activeId = selected === "new" ? undefined : (selected ?? convs?.[0]?.id);
  const activeConv = convs?.find((c) => c.id === activeId);
  // Phiên rỗng (bản cũ tạo sẵn khi mở AI Tutor) không hiện trong danh sách, trừ phiên đang mở.
  const sessions = (convs ?? []).filter((c) => c.message_count > 0 || c.id === activeId);

  const { data: messagesPage } = useMessages(activeId);
  const messages = activeId ? (messagesPage?.results ?? []) : [];
  const {
    partial,
    citations: partialCitations,
    streaming,
    error,
    send,
    reset,
  } = useStreamingChat(activeId);

  const selectSession = (id: number | "new") => {
    reset();
    setSelected(id);
    setView("chat");
  };

  const startNewSession = () => {
    // Phiên đang mở còn trống → dùng luôn, không mở thêm.
    if (!activeId || activeConv?.message_count === 0) setView("chat");
    else selectSession("new");
  };

  const handleSend = async (text: string) => {
    if (streaming || createConv.isPending) return;
    if (activeId) {
      void send(text);
      return;
    }
    // Phiên mới: dùng lại phiên rỗng sẵn có của bài này, không có thì tạo.
    let id = lessonSlug ? convs?.find((c) => c.message_count === 0)?.id : undefined;
    if (!id) {
      try {
        id = (await createConv.mutateAsync(lessonSlug ?? null)).id;
      } catch {
        message.error("Không tạo được phiên chat mới, vui lòng thử lại.");
        return;
      }
    }
    setSelected(id);
    void send(text, id);
  };

  const busy = streaming || createConv.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={width}
      title={
        <Space direction="vertical" size={0} style={{ width: "100%" }}>
          <Space>
            <RobotOutlined style={{ color: "#722ed1" }} />
            <Title level={5} style={{ margin: 0 }}>
              AI Tutor
            </Title>
          </Space>
          {lessonTitle && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {lessonTitle}
            </Text>
          )}
        </Space>
      }
      extra={
        <Space size={4}>
          <Tooltip title={busy ? "Chờ AI trả lời xong" : "Bắt đầu phiên chat mới"}>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={startNewSession}
              disabled={busy}
              data-testid="ai-tutor-new-session"
            >
              Phiên mới
            </Button>
          </Tooltip>
          <Tooltip title={busy ? "Chờ AI trả lời xong" : "Xem lại các phiên chat"}>
            <Button
              size="small"
              type={view === "sessions" ? "primary" : "default"}
              icon={<HistoryOutlined />}
              onClick={() => setView((v) => (v === "sessions" ? "chat" : "sessions"))}
              disabled={busy}
              data-testid="ai-tutor-sessions-btn"
            >
              Các phiên{sessions.length > 0 ? ` (${sessions.length})` : ""}
            </Button>
          </Tooltip>
        </Space>
      }
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column" },
      }}
      data-testid="ai-tutor-panel"
    >
      {view === "sessions" ? (
        <SessionList
          sessions={sessions}
          activeId={activeId}
          loading={convsLoading}
          onSelect={selectSession}
          onNew={startNewSession}
        />
      ) : (
        <>
          {error && (
            <Alert
              type="error"
              showIcon
              message="Lỗi gửi câu hỏi"
              description={error}
              closable
              style={{ margin: 12 }}
            />
          )}
          <MessageList
            messages={messages}
            partial={partial}
            partialCitations={partialCitations}
            streaming={streaming}
            suggestedQuestions={suggestedQuestions}
            onSuggestionClick={(text) => void handleSend(text)}
          />
        </>
      )}
      {/* Giữ ChatInput khi xem các phiên — không mất câu đang soạn, không tạo lại editor. */}
      <div style={{ display: view === "chat" ? undefined : "none" }}>
        <ChatInput
          onSend={(text) => void handleSend(text)}
          disabled={busy || convsLoading}
          status={
            streaming
              ? "Đang nhận trả lời..."
              : createConv.isPending
                ? "Đang tạo phiên chat..."
                : convsLoading
                  ? "Đang tải phiên chat..."
                  : undefined
          }
        />
      </div>
    </Drawer>
  );
}

export default AITutorPanel;
